import { useState, useEffect, useCallback, useRef } from 'react';
import { Message } from '../types';
import { chatAPI, historyAPI, voiceAPI, imageAPI } from '../api';
import { SupportedLang } from "../api/translations"; // 👈 add import

interface UseChatProps {
  user: any;
  refreshUser: () => void;
  updateUsage: (usage: any, limits: any) => void;
}

interface UseChatOptions {
  user?: any;
  refreshUser?: () => void;
  updateUsage?: (usage: any, limits: any) => void;
  limits?: {
    text_messages: number;
    voice_minutes: number;
    image_uploads: number;
  };
  language?: SupportedLang; // ✅ add this
}

export const useChat = ({ user, refreshUser, updateUsage, limits, language }: UseChatOptions = {}) => {
  // Provide default limits if not provided
  const defaultLimits = {
    text_messages: 6,
    voice_minutes: 6,
    image_uploads: 6
  };
  const effectiveLimits = limits || defaultLimits;

    const languageRef = useRef<SupportedLang>('en');
  useEffect(() => {
    if (language) languageRef.current = language;
  }, [language]);
  
  // Add state to store dynamic limits from backend response
  const [dynamicLimits, setDynamicLimits] = useState<{
    text_messages: number;
    voice_minutes: number;
    image_uploads: number;
  } | null>(null);
  
  const [messages, setMessages] = useState<Message[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [isRecording, setIsRecording] = useState(false);
  const [isPaused, setIsPaused] = useState(false);
  const [recordingTime, setRecordingTime] = useState(0);
  const [mediaRecorder, setMediaRecorder] = useState<MediaRecorder | null>(null);
  const audioChunksRef = useRef<Blob[]>([]);
 const recordingTimerRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const historyLoadedRef = useRef(false);
  const isCancellingRef = useRef(false);
  
  // Add guest usage tracking
  const [guestUsage, setGuestUsage] = useState<{ messagesUsed: number; messagesRemaining: number } | null>(null);
  
  // Add flag to track if claiming is in progress
  const [isClaimingGuest, setIsClaimingGuest] = useState(false);

function generateGuestId() {
  if (window.crypto?.randomUUID) {
    return window.crypto.randomUUID();
  }

  // fallback → use getRandomValues to generate UUID-like string
  const array = new Uint32Array(4);
  window.crypto.getRandomValues(array);
  return (
    array[0].toString(16) +
    array[1].toString(16) +
    array[2].toString(16) +
    array[3].toString(16)
  );
}

  // Generate and store guest_id for unauthenticated users
  const [guestId, setGuestId] = useState<string | null>(() => {
    if (typeof window !== 'undefined') {
      const stored = localStorage.getItem('guest_id');
      if (stored) return stored;


const newGuestId = generateGuestId();
localStorage.setItem("guest_id", newGuestId);

      return newGuestId;
    }
    return null;
  });

  // Don't clear guest_id immediately when user logs in
  // Let the claim process handle clearing it after successful transfer
  // This ensures the auto-claim useEffect can trigger properly


const sendMessage = useCallback(
  async (payload: { 
    message: string; 
    guest_id?: string | null; 
    language?: SupportedLang;
    user_type?: "guest" | "user" | "admin" | "super-admin" | "basic" | "premium" | "ultimate"; // ✅ added
  }) => {
    if (!payload.message.trim()) return;

    // Add user message immediately
    const userMessage: Message = {
      id: Date.now(),
      type: "text",
      userMessage: payload.message,
      timestamp: new Date().toISOString(),
    };
    setMessages((prev) => [...prev, userMessage]);

    setLoading(true);
    setError(null);

    try {
      // Prepare request payload
      const apiPayload: any = { message: payload.message };

      if (payload.language) {
        apiPayload.language = payload.language;
      }

      if (!user && (payload.guest_id || guestId)) {
        apiPayload.guest_id = payload.guest_id || guestId;
      }

      // ✅ Add user_type
      if (payload.user_type) {
        console.log('User type:', payload.user_type);
        apiPayload.user_type = payload.user_type;
      } else if (user?.user_type) {
        apiPayload.user_type = user.user_type;
      } else {
        apiPayload.user_type = "guest"; // fallback
      }

      const response = await chatAPI.sendMessage(apiPayload);

      // Add bot response
      const botMessage: Message = {
        id: Date.now() + 1,
        type: "text",
        userMessage: "",
        botResponse: response.response || response.botResponse,
        timestamp: response.timestamp,
        recommendedVideos: response.recommended_videos,
        video_recommendations: response.recommended_videos,
      };
      setMessages((prev) => [...prev, botMessage]);

      if (user && response.usage && response.limits && updateUsage) {
        updateUsage(response.usage, response.limits);
      }

      if (!user && guestId && response.guestInfo && response.limits) {
        setGuestUsage(response.guestInfo);
        setDynamicLimits(response.limits);
      }

      return response;
    } catch (err: any) {
      console.error("Error sending message:", err);
      setError(err.response?.data?.message || "Failed to send message");
      setMessages((prev) => prev.filter((msg) => msg.id !== userMessage.id));
      throw err;
    } finally {
      setLoading(false);
    }
  },
  [user?.id, user?.email, guestId, updateUsage, effectiveLimits]
);


  // Load history when user or guestId changes
  useEffect(() => {
    console.log('useChat: History loading useEffect triggered', { 
      user: !!user, 
      guestId: !!guestId, 
      isClaimingGuest, 
      historyLoadedRef: historyLoadedRef.current 
    });
    
    // Don't load history if claiming is in progress
    if (isClaimingGuest) {
      console.log('useChat: Skipping history load - claiming in progress');
      return;
    }
    
    // Don't load user history if guestId is still present (claim process not complete)
    if (user && guestId) {
      console.log('useChat: Skipping user history load - guestId still present, waiting for claim to complete');
      return;
    }
    
    // Reset historyLoadedRef when user changes from guest to authenticated
    if (user && !guestId && !historyLoadedRef.current) {
      console.log('useChat: User authenticated without guestId, resetting historyLoadedRef');
      historyLoadedRef.current = false;
    }
    
    if ((user || guestId) && !historyLoadedRef.current) {
      console.log('useChat: Loading history', { user: !!user, guestId: !!guestId });
      historyLoadedRef.current = true;
      const loadHistory = async () => {
        setLoading(true);
        setError(null);
        try {
          let response;
          if (!user && guestId) {
            console.log('useChat: Loading guest history');
            response = await historyAPI.getHistory({ guest_id: guestId });
          } else {
            console.log('useChat: Loading user history');
            response = await historyAPI.getHistory();
          }
          if (response && response.messages) {
            console.log('useChat: History loaded', { messageCount: response.messages.length });
            const transformedMessages = response.messages.map((msg: any) => ({
              id: msg.id,
              type: msg.type || 'text',
              userMessage: msg.userMessage || '',
              botResponse: msg.botResponse || '',
              timestamp: msg.timestamp,
              transcription: msg.transcription,
              audioUrl: msg.audioUrl || msg.audio_url,
              userAudioUrl: msg.userAudioUrl || msg.user_audio_url,
              imageUrl: msg.imageUrl || msg.image_url,
              imageDescription: msg.imageDescription || msg.image_description
            }));
            setMessages(transformedMessages.reverse());
            
            // For guest users, calculate current usage based on loaded messages
            if (!user && guestId) {
              const messageCount = transformedMessages.length;
              
              // Use dynamic limits from history API response if available
              if (response.guestInfo && response.limits) {
                setGuestUsage(response.guestInfo);
                setDynamicLimits(response.limits);
              } else {
                // Fallback to calculated usage with current limits
                const currentLimits = dynamicLimits || effectiveLimits;
                const freeLimit = currentLimits.text_messages !== -1 ? currentLimits.text_messages : 6;
                setGuestUsage({
                  messagesUsed: messageCount,
                  messagesRemaining: Math.max(0, freeLimit - messageCount)
                });
              }
            }
          }
        } catch (err: any) {
          console.error('useChat: Error loading history:', err);
          setError(err.response?.data?.message || 'Failed to load chat history');
        } finally {
          setLoading(false);
        }
      };
      loadHistory();
    }
  }, [user?.id, user?.email, guestId, isClaimingGuest, effectiveLimits, dynamicLimits]);

  // Clear all messages (for logout)
  const clearMessages = useCallback(() => {
    setMessages([]);
    setError(null);
    setLoading(false);
    setIsRecording(false);
    setIsPaused(false);
    setRecordingTime(0);
    if (mediaRecorder) {
      mediaRecorder.stop();
      setMediaRecorder(null);
    }
    audioChunksRef.current = [];
    if (recordingTimerRef.current) {
      clearInterval(recordingTimerRef.current);
      recordingTimerRef.current = null;
    }
    // Reset history loaded flag so history can be reloaded if user logs back in
    historyLoadedRef.current = false;
  }, [mediaRecorder]);

  // Listen for logout event to clear messages
  useEffect(() => {
    const handleLogout = () => {
      console.log('useChat: User logged out, clearing messages');
      clearMessages();
    };

    window.addEventListener('userLoggedOut', handleLogout);
    return () => window.removeEventListener('userLoggedOut', handleLogout);
  }, [clearMessages]);

  const claimGuestConversation = useCallback(async () => {
    if (!user || !guestId) {
      console.log('claimGuestConversation: Cannot claim - missing user or guestId', { user: !!user, guestId: !!guestId });
      return;
    }

    // If guestUsage is null (cleared during authentication), fetch it from backend
    let currentGuestUsage = guestUsage;
    if (!currentGuestUsage && guestId) {
      console.log('claimGuestConversation: Guest usage is null, fetching from backend');
      try {
        currentGuestUsage = await getGuestUsage(guestId);
        console.log('claimGuestConversation: Fetched guest usage from backend:', currentGuestUsage);
      } catch (error) {
        console.error('claimGuestConversation: Error fetching guest usage:', error);
        // If we can't fetch usage, assume they haven't hit the limit
        currentGuestUsage = { messagesUsed: 0, messagesRemaining: 6 };
      }
    }

    // Check if guest has hit their limit before attempting to claim
    // Allow claiming in both scenarios: when guest hits limit OR when user signs up
    if (currentGuestUsage && currentGuestUsage.messagesRemaining > 0) {
      console.log('claimGuestConversation: Guest has not hit limit yet, but allowing claim for signup', { 
        messagesRemaining: currentGuestUsage.messagesRemaining 
      });
      // Don't skip claim - allow it to proceed even if guest hasn't hit limit
    }

    // Proceed with claiming regardless of whether guest hit limit or not
    console.log('claimGuestConversation: Proceeding with claim (guest may or may not have hit limit)', { 
      currentGuestUsage,
      messagesRemaining: currentGuestUsage?.messagesRemaining 
    });

    try {
      console.log('claimGuestConversation: Starting claim process', { 
        guestId, 
        userId: user.id,
        currentGuestUsage,
        messagesRemaining: currentGuestUsage?.messagesRemaining 
      });
      setIsClaimingGuest(true);
      const response = await chatAPI.claimGuestConversation(guestId);
      console.log('Guest conversation claimed successfully:', response);
      
      // Clear guest_id from localStorage
      localStorage.removeItem('guest_id');
      setGuestId(null);
      setGuestUsage(null); // Clear guest usage
      
      // Reset historyLoadedRef so user history gets loaded after claiming
      historyLoadedRef.current = false;
      console.log('claimGuestConversation: Reset historyLoadedRef, calling refreshUser');
      
      // Always refresh user after claiming; useEffect will reload chat history
      if (refreshUser) {
        await refreshUser();
      }
      console.log('claimGuestConversation: refreshUser completed');
    } catch (err: any) {
      console.error('Error claiming guest conversation:', err);
      const errorMessage = err.response?.data?.message || 'Failed to claim guest conversation';
      setError(errorMessage);
      
      // If the error indicates the guest didn't hit their limit, clear the guest ID
      if (errorMessage.includes('Guest limit not hit') || errorMessage.includes('Guest conversation can only be claimed')) {
        localStorage.removeItem('guest_id');
        setGuestId(null);
        setGuestUsage(null);
      }
    } finally {
      setIsClaimingGuest(false);
      console.log('claimGuestConversation: Completed, isClaimingGuest set to false');
    }
  }, [user, guestId, guestUsage, refreshUser]);

const sendVoiceMessage = useCallback(
  async (audioFile: File) => {
    setLoading(true);
    setError(null);

    // Optimistic UI: show user's local recording preview
    const userVoiceMessage: Message = {
      id: Date.now(),
      type: 'voice',
      userMessage: 'Recording...',
      timestamp: new Date().toISOString(),
      transcription: 'Processing...',
      audioUrl: URL.createObjectURL(audioFile),
    };
    setMessages(prev => [...prev, userVoiceMessage]);

    const applyResponse = (data: any) => {
      const updatedUser: Message = {
        ...userVoiceMessage,
        userMessage: data.transcription || 'Voice message',
        transcription: data.transcription,
        userAudioUrl: userVoiceMessage.audioUrl,
      };
      const botVoice: Message = {
        id: Date.now() + 1,
        type: 'voice',
        userMessage: '',
        botResponse: data.botResponse,
        timestamp: data.timestamp || new Date().toISOString(),
        audioUrl: data.audioUrl ?? null,
      };
      setMessages(prev => {
        const filtered = prev.filter(m => m.id !== userVoiceMessage.id);
        return [...filtered, updatedUser, botVoice];
      });
      if (data.usage && data.limits && updateUsage) { try { updateUsage(data.usage, data.limits); } catch {} }
      if (!user && data.guestInfo) { try { setGuestUsage(data.guestInfo); } catch {} }
    };

    try {
      // ✅ now pass language to your API
      const resp = await voiceAPI.sendVoice(
        audioFile,
        guestId || user?.id,
        languageRef.current  || 'en'
      );
      applyResponse(resp);
    } catch (err: any) {
      console.error('Error sending voice message:', err);
      const msg =
        err?.response?.data?.detail ||
        err?.response?.data?.message ||
        err?.message ||
        'Failed to send voice message';
      setError(msg);
      setMessages(prev => prev.filter(m => m.id !== userVoiceMessage.id));
      throw err;
    } finally {
      setLoading(false);
    }
  },
  [user?.id, user?.email, guestId, updateUsage]
);



const startRecording = useCallback(async () => {
  console.log('startRecording called');
  audioChunksRef.current = [];
  setIsPaused(false);
  setRecordingTime(0);

  try {
    const stream = await navigator.mediaDevices.getUserMedia({ audio: true });

    // Pick supported MIME
    let mimeType = '';
    const candidates = [
      'audio/webm;codecs=opus',
      'audio/webm',
      'audio/ogg;codecs=opus',
      'audio/mp4',
    ];
    for (const c of candidates) {
      if ((window as any).MediaRecorder?.isTypeSupported?.(c)) { mimeType = c; break; }
    }

    const recorder = new MediaRecorder(stream, mimeType ? { mimeType } : undefined);

    recorder.ondataavailable = (e) => {
      if (e?.data && e.data.size > 0) audioChunksRef.current.push(e.data);
    };

    recorder.onstop = async () => {
      try {
        if (isCancellingRef.current) {
          isCancellingRef.current = false;
          audioChunksRef.current = [];
          return;
        }

        if (!audioChunksRef.current.length) {
          setError('No audio captured. Please allow microphone and try again.');
          return;
        }

        const rawBlob = new Blob(audioChunksRef.current, { type: mimeType || 'audio/webm' });
        console.log('Audio recorded:', { size: rawBlob.size, type: rawBlob.type });

        // ✅ ALWAYS convert to WAV (16k mono PCM)
        const wavBlob = await convertAudioToWav(rawBlob);
        if (!wavBlob || wavBlob.type !== 'audio/wav') {
          throw new Error('WAV conversion failed');
        }
        const wavFile = new File([wavBlob], 'voice-message.wav', { type: 'audio/wav' });

        await sendVoiceMessage(wavFile);
        audioChunksRef.current = [];
      } catch (err: any) {
        console.error('Error processing voice message:', err);
        setError(
          err?.response?.data?.detail ||
          err?.response?.data?.message ||
          err?.message ||
          'Failed to process voice message. Please try again.'
        );
      } finally {
        try { recorder.stream.getTracks().forEach(t => t.stop()); } catch {}
      }
    };

    setMediaRecorder(recorder);
    recorder.start();
    setIsRecording(true);

    recordingTimerRef.current = setInterval(() => setRecordingTime(p => p + 1), 1000);
  } catch (err) {
    console.error('Error starting recording:', err);
    setError('Failed to start recording. Please check microphone permissions.');
  }
}, [sendVoiceMessage]);


  const stopRecording = useCallback(() => {
    console.log('stopRecording called');
    if (mediaRecorder && isRecording) {
      mediaRecorder.stop();
      mediaRecorder.stream.getTracks().forEach(track => track.stop());
      setMediaRecorder(null);
      setIsRecording(false);
      setIsPaused(false);
      
      // Clear recording timer
      if (recordingTimerRef.current) {
        clearInterval(recordingTimerRef.current);
        recordingTimerRef.current = null;
      }
      setRecordingTime(0);
    }
  }, [mediaRecorder, isRecording]);

  const pauseRecording = useCallback(() => {
    console.log('pauseRecording called');
    if (mediaRecorder && isRecording && !isPaused) {
      mediaRecorder.pause();
      setIsPaused(true);
      
      // Pause recording timer
      if (recordingTimerRef.current) {
        clearInterval(recordingTimerRef.current);
        recordingTimerRef.current = null;
      }
    }
  }, [mediaRecorder, isRecording, isPaused]);

  const resumeRecording = useCallback(() => {
    console.log('resumeRecording called');
    if (mediaRecorder && isRecording && isPaused) {
      mediaRecorder.resume();
      setIsPaused(false);
      
      // Resume recording timer
      recordingTimerRef.current = setInterval(() => {
        setRecordingTime(prev => prev + 1);
      }, 1000);
    }
  }, [mediaRecorder, isRecording, isPaused]);

  const cancelRecording = useCallback(() => {
    console.log('cancelRecording called');
    if (mediaRecorder) {
      isCancellingRef.current = true;
      mediaRecorder.stop();
      mediaRecorder.stream.getTracks().forEach(track => track.stop());
      setMediaRecorder(null);
      setIsRecording(false);
      setIsPaused(false);
      audioChunksRef.current = [];
      
      // Clear recording timer
      if (recordingTimerRef.current) {
        clearInterval(recordingTimerRef.current);
        recordingTimerRef.current = null;
      }
      setRecordingTime(0);
    }
  }, [mediaRecorder]);

  // Client-side audio conversion function
const convertAudioToWav = async (audioBlob: Blob): Promise<Blob> => {
  return new Promise((resolve, reject) => {
    try {
      const AudioCtx = (window.AudioContext || (window as any).webkitAudioContext);
      if (!AudioCtx) {
        return reject(new Error('Web Audio not supported'));
      }
      const audioContext = new AudioCtx();
      const reader = new FileReader();

      reader.onload = async (event) => {
        try {
          const arrayBuffer = event.target?.result as ArrayBuffer;
          const audioBuffer = await audioContext.decodeAudioData(arrayBuffer);

          const offline = new OfflineAudioContext(
            1, // mono
            Math.ceil(audioBuffer.length * (16000 / audioBuffer.sampleRate)), // resample
            16000 // 16kHz
          );

          const source = offline.createBufferSource();
          source.buffer = audioBuffer;
          source.connect(offline.destination);
          source.start(0);

          const rendered = await offline.startRendering();

          const wavBlob = audioBufferToWav(rendered, { sampleRate: 16000, channels: 1, bitDepth: 16 });
          if (!wavBlob || wavBlob.type !== 'audio/wav') {
            return reject(new Error('WAV conversion produced invalid blob'));
          }
          console.log('Audio conversion successful:', {
            sampleRate: rendered.sampleRate,
            channels: rendered.numberOfChannels,
            duration: rendered.length / rendered.sampleRate,
            size: wavBlob.size
          });
          resolve(wavBlob);
        } catch (e) {
          console.error('Audio conversion failed:', e);
          reject(new Error('WAV conversion failed'));
        }
      };

      reader.onerror = (e) => reject(e);
      reader.readAsArrayBuffer(audioBlob);
    } catch (e) {
      reject(new Error('Audio conversion not supported'));
    }
  });
};

  // Helper function to convert AudioBuffer to WAV with configurable settings
  const audioBufferToWav = (buffer: AudioBuffer, options: {
    sampleRate?: number;
    channels?: number;
    bitDepth?: number;
  } = {}): Blob => {
    const {
      sampleRate = buffer.sampleRate,
      channels = buffer.numberOfChannels,
      bitDepth = 16
    } = options;
    
    const length = buffer.length;
    const bytesPerSample = bitDepth / 8;
    const blockAlign = channels * bytesPerSample;
    const dataSize = length * blockAlign;
    const arrayBuffer = new ArrayBuffer(44 + dataSize);
    const view = new DataView(arrayBuffer);
    
    // WAV header
    const writeString = (offset: number, string: string) => {
      for (let i = 0; i < string.length; i++) {
        view.setUint8(offset + i, string.charCodeAt(i));
      }
    };
    
    writeString(0, 'RIFF');
    view.setUint32(4, 36 + dataSize, true);
    writeString(8, 'WAVE');
    writeString(12, 'fmt ');
    view.setUint32(16, 16, true);
    view.setUint16(20, 1, true); // PCM format
    view.setUint16(22, channels, true);
    view.setUint32(24, sampleRate, true);
    view.setUint32(28, sampleRate * blockAlign, true);
    view.setUint16(32, blockAlign, true);
    view.setUint16(34, bitDepth, true);
    writeString(36, 'data');
    view.setUint32(40, dataSize, true);
    
    // Convert audio data
    let offset = 44;
    for (let i = 0; i < length; i++) {
      for (let channel = 0; channel < channels; channel++) {
        const sample = Math.max(-1, Math.min(1, buffer.getChannelData(channel)[i]));
        
        if (bitDepth === 16) {
          view.setInt16(offset, sample < 0 ? sample * 0x8000 : sample * 0x7FFF, true);
        } else if (bitDepth === 8) {
          view.setUint8(offset, (sample + 1) * 0.5 * 255);
        }
        
        offset += bytesPerSample;
      }
    }
    
    return new Blob([arrayBuffer], { type: 'audio/wav' });
  };

const sendImageMessage = useCallback(
  async (imageFile: File, description: string, language?: SupportedLang) => {
    try {
      setLoading(true);
      setError(null);

      // Create user image message immediately (before API call)
      const userImageMessage: Message = {
        id: Date.now(),
        type: 'image',
        userMessage: description,
        timestamp: new Date().toISOString(),
        imageUrl: URL.createObjectURL(imageFile),
        imageDescription: description,
      };

      // Add user message immediately
      setMessages(prev => [...prev, userImageMessage]);

      let retryCount = 0;
      const maxRetries = 1;
      let lastError = null;

      while (retryCount <= maxRetries) {
        try {
          console.log(`Attempting image upload (attempt ${retryCount + 1}/${maxRetries + 1})`);

          // ✅ Pass language here
      const response = await imageAPI.sendImage(
        imageFile,
        description,
        guestId || undefined,
        language ?? languageRef.current ?? 'en'  // ✅ ensure language always sent
      );

          // Create bot image message
          const botImageMessage: Message = {
            id: Date.now() + 1,
            type: 'image',
            userMessage: '',
            botResponse: response.analysis_text,
            timestamp: response.timestamp || new Date().toISOString(),
            audioUrl: response.audio_url,
          };

          // Update user message with permanent S3 URL
          const updatedUserMessage: Message = {
            ...userImageMessage,
            imageUrl: response.imageUrl, // Use permanent S3 URL instead of blob URL
          };

          console.log('Image message response received:', {
            description,
            analysisText: response.analysis_text?.substring(0, 50) + '...',
            audioUrl: response.audio_url,
            imageUrl: response.imageUrl,
            usage: response.usage,
            limits: response.limits,
          });

          // Update messages
          setMessages(prev => {
            const filtered = prev.filter(msg => msg.id !== userImageMessage.id);
            return [...filtered, updatedUserMessage, botImageMessage];
          });

          // Update usage (authenticated users)
          if (response.usage && response.limits && updateUsage) {
            try {
              updateUsage(response.usage, response.limits);
            } catch (usageError) {
              console.error('Error updating usage for image:', usageError);
            }
          }

          // Update guest usage
          if (!user && response.guestInfo) {
            try {
              setGuestUsage(response.guestInfo);
            } catch (guestError) {
              console.error('Error updating guest usage for image:', guestError);
            }
          }

          // Success, break loop
          break;
        } catch (err: any) {
          lastError = err;
          retryCount++;

          console.error(`Image upload attempt ${retryCount} failed:`, err);

          if (
            retryCount <= maxRetries &&
            (err.message?.includes('timeout') ||
              err.message?.includes('longer than expected') ||
              err.response?.status === 408)
          ) {
            console.log(`Retrying image upload due to timeout...`);
            await new Promise(resolve => setTimeout(resolve, 2000));
            continue;
          }
          break;
        }
      }


      // If we get here and lastError exists, all retries failed
      if (lastError) {
        console.error('All image upload attempts failed:', lastError);
        
        // Handle different types of errors with better messages
        let errorMessage = 'Failed to send image message';
        let errorSuggestion = '';
        
        if (lastError.response?.data) {
          // Backend error with structured response
          const errorData = lastError.response.data;
          errorMessage = errorData.message || errorData.error || 'Failed to send image message';
          errorSuggestion = errorData.suggestion || '';
        } else if (lastError.message) {
          // Frontend error
          if (lastError.message.includes('AI service is taking longer than expected to respond')) {
            errorMessage = 'AI service is taking longer than expected to respond. This may be due to high server load. Please try again in a moment.';
            errorSuggestion = 'Please try again in a moment';
          } else if (lastError.message.includes('timeout') || lastError.message.includes('longer than expected')) {
            errorMessage = 'Image analysis is taking longer than expected. Please try again with a smaller image or check your internet connection.';
            errorSuggestion = 'Try uploading a smaller image or check your internet connection';
          } else if (lastError.message.includes('AI service is temporarily unavailable')) {
            errorMessage = 'AI service is temporarily unavailable. Please try again in a few minutes.';
            errorSuggestion = 'Please try again in a few minutes';
          } else if (lastError.message.includes('Failed to connect')) {
            errorMessage = 'Failed to connect to AI service. Please check your internet connection and try again.';
            errorSuggestion = 'Check your internet connection and try again';
          } else if (lastError.message.includes('File too large')) {
            errorMessage = 'Image file is too large. Please use an image smaller than 5MB.';
            errorSuggestion = 'Use an image smaller than 5MB';
          } else if (lastError.message.includes('Invalid file type')) {
            errorMessage = 'Only image files (JPG, PNG, GIF, WebP) are allowed.';
            errorSuggestion = 'Use a supported image format';
          } else {
            errorMessage = lastError.message;
          }
        }
        
        // Combine message and suggestion if available
        const fullErrorMessage = errorSuggestion ? `${errorMessage} ${errorSuggestion}` : errorMessage;
        setError(fullErrorMessage);
        
        // Before removing the message, check if it was actually processed by the backend
        // This handles the case where frontend times out but backend succeeds
        try {
          console.log('Checking if image message was processed by backend...');
          const historyResponse = await historyAPI.getHistory({ 
            limit: 10, 
            guest_id: guestId || undefined 
          });
          
          // Look for our message in recent history
          const recentMessages = historyResponse.messages || [];
          const processedMessage = recentMessages.find((msg: any) => 
            msg.type === 'image' && 
            msg.user_message === description &&
            new Date(msg.timestamp).getTime() > userImageMessage.id - 10000 // Within 10 seconds
          );
          
          if (processedMessage) {
            console.log('Found processed image message in history:', processedMessage);
            // The message was processed! Update our local state with the real data
            const updatedUserMessage: Message = {
              ...userImageMessage,
              id: processedMessage.id,
              imageUrl: (processedMessage as any).image_url || userImageMessage.imageUrl,
              timestamp: processedMessage.timestamp,
            };
            
            const botMessage: Message = {
              id: processedMessage.id + 1,
              type: 'image',
              userMessage: '',
              botResponse: (processedMessage as any).bot_response,
              timestamp: processedMessage.timestamp,
              audioUrl: (processedMessage as any).audio_url,
            };
            
            // Update messages with the real data from backend
            setMessages(prev => {
              const filtered = prev.filter(msg => msg.id !== userImageMessage.id);
              return [...filtered, updatedUserMessage, botMessage];
            });
            
            // Clear any error since it was actually successful
            setError(null);
            return; // Don't throw error since it was successful
          }
        } catch (historyError) {
          console.error('Error checking history for processed message:', historyError);
        }
        
        // If we get here, the message wasn't processed, so remove it and show error
        setMessages(prev => prev.filter(msg => msg.id !== userImageMessage.id));
        
        throw lastError;
      }
    } finally {
      setLoading(false);
    }
  }, [user?.id, user?.email, guestId, updateUsage]);

  const clearError = useCallback(() => {
    setError(null);
  }, []);

  // Auto-claim guest conversation when user becomes authenticated
  useEffect(() => {
    const shouldClaim = user && guestId && !isClaimingGuest;
    
    if (shouldClaim) {
      // Check if user signed up through chat widget (not navigation bar)
      // We can detect this by checking if the chat widget is currently open
      const chatWidget = document.querySelector('[data-testid="chat-widget"]');
      const isChatWidgetOpen = chatWidget && chatWidget.classList.contains('open');
      
      // Check if AuthForm modal is open (navigation bar login)
      const authModal = document.querySelector('[style*="z-index: 20000"]') as HTMLElement;
      const isAuthModalOpen = authModal && authModal.style.display !== 'none';
      
      console.log('useChat: User authenticated, checking claim conditions', { 
        userId: user.id, 
        guestId,
        isClaimingGuest,
        isChatWidgetOpen,
        isAuthModalOpen,
        chatWidgetFound: !!chatWidget,
        chatWidgetClasses: chatWidget ? chatWidget.className : 'not found',
        authModalFound: !!authModal
      });
      
      // Only claim if chat widget is open AND auth modal is not open
      // (user signed up through chat widget, not navigation bar)
      if (isChatWidgetOpen && !isAuthModalOpen) {
        console.log('useChat: Chat widget is open and auth modal is not open, proceeding with auto-claim');
        
        // Use setTimeout to ensure user state is fully updated
        const timer = setTimeout(() => {
          claimGuestConversation();
        }, 1000); // Increased delay to ensure user state is fully updated
        
        return () => clearTimeout(timer);
      } else {
        console.log('useChat: Skipping auto-claim - chat widget not open or auth modal is open (user signed in through navigation bar)');
        // Clear guest data since user signed in through navigation bar
        localStorage.removeItem('guest_id');
        setGuestId(null);
        setGuestUsage(null);
      }
    }
  }, [user?.id, user?.email, guestId, isClaimingGuest, claimGuestConversation]);

  // Update guest message count calculation
  const getGuestMessageCount = async (guestId: string) => {
    try {
      const response = await historyAPI.getHistory({ guest_id: guestId });
      return response.messages.length;
    } catch (error) {
      console.error('Error getting guest message count:', error);
      return 0;
    }
  };

  const getGuestUsage = async (guestId: string) => {
    try {
      const messageCount = await getGuestMessageCount(guestId);
      // Use dynamicLimits if available, otherwise fallback to effectiveLimits
      const currentLimits = dynamicLimits || effectiveLimits;
      const freeLimit = currentLimits.text_messages !== -1 ? currentLimits.text_messages : 6;
      const usage = {
        messagesUsed: messageCount,
        messagesRemaining: Math.max(0, freeLimit - messageCount)
      };
      console.log('getGuestUsage: Calculated usage:', { 
        guestId, 
        messageCount, 
        freeLimit, 
        usage 
      });
      return usage;
    } catch (error) {
      console.error('Error getting guest usage:', error);
      const currentLimits = dynamicLimits || effectiveLimits;
      const freeLimit = currentLimits.text_messages !== -1 ? currentLimits.text_messages : 6;
      return {
        messagesUsed: 0,
        messagesRemaining: freeLimit
      };
    }
  };

  return {
    messages,
    loading,
    error,
    sendMessage,
    sendVoiceMessage,
    sendImageMessage,
    startRecording,
    stopRecording,
    pauseRecording,
    resumeRecording,
    cancelRecording,
    isRecording,
    isPaused,
    recordingTime,
    claimGuestConversation,
    guestId,
    guestUsage,
    clearError,
    clearMessages
  };
}; 